import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * How long a cart trigger waits for Rebuy's Smart Cart before showing the
 * theme's own drawer instead. Rebuy loads after the page (a few seconds on a
 * fast connection, longer on phones), so an early tap must wait for it rather
 * than open a second, different cart.
 */
const REBUY_WAIT_MS = 8000;

/** @returns {boolean} */
const rebuyCartReady = () => typeof window.Rebuy?.SmartCart?.show === 'function';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /** @type {Promise<boolean> | null} */
  #rebuyWait = null;

  open() {
    /**
     * Rebuy's Smart Cart is the store's cart UI (its own script already opens it
     * automatically after a native /cart/add.js request). Route this trigger to the
     * same drawer instead of the theme's native dialog, so the header cart icon and
     * "add to cart" always show the same cart.
     *
     * Rebuy loads after the page, so a tap in the first seconds would find no
     * Smart Cart yet. Wait for it (`rebuy:smartcart.ready`, checked by polling
     * too in case the event fired before this listener) and open it then. Only
     * if Rebuy never loads (app disabled, script blocked) does the native dialog
     * open, after REBUY_WAIT_MS.
     *
     * #handleCartAdd must call open() (not showDialog() directly) for this to work -
     * otherwise the native dialog auto-opens on every add-to-cart in addition to (not
     * instead of) Rebuy's own auto-opened Smart Cart, showing two overlapping carts.
     */
    if (rebuyCartReady()) {
      window.Rebuy.SmartCart.show();
      return;
    }

    // The theme editor previews the theme's own drawer; Rebuy does not run there.
    if (window.Shopify?.designMode) {
      this.#showNativeDialog();
      return;
    }

    // Repeated taps while waiting share one wait and open one cart.
    if (this.#rebuyWait) return;

    this.#rebuyWait = this.#waitForRebuy().then((ready) => {
      this.#rebuyWait = null;
      if (ready) {
        window.Rebuy.SmartCart.show();
      } else {
        this.#showNativeDialog();
      }
      return ready;
    });
  }

  /**
   * Resolves true once Rebuy's Smart Cart can be shown, false after REBUY_WAIT_MS.
   *
   * @returns {Promise<boolean>}
   */
  #waitForRebuy() {
    return new Promise((resolve) => {
      const finish = (/** @type {boolean} */ ready) => {
        clearInterval(poll);
        clearTimeout(timeout);
        document.removeEventListener('rebuy:smartcart.ready', onReady);
        resolve(ready);
      };
      const onReady = () => finish(rebuyCartReady());
      const poll = setInterval(() => {
        if (rebuyCartReady()) finish(true);
      }, 100);
      const timeout = setTimeout(() => finish(rebuyCartReady()), REBUY_WAIT_MS);
      document.addEventListener('rebuy:smartcart.ready', onReady);
    });
  }

  #showNativeDialog() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
